import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import TraceTable from "../TraceTable.jsx";
import { KernelBlock } from "../kernelPrimitives/index.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useQuery } from "../../lib/dataClient.js";
import { useSignalCatalog } from "../../lib/signalCatalog.js";
import { fetchSignalBadges } from "../../lib/signalsDim.js";
import { hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";

// Signals dimension lens — the "alert console". Signals are READ from the
// backend (signal_tags), so this is just a scoped trace view: the prompts that
// fired any ALERT signal across the current scope, server-paginated (no
// in-browser scan, no window scrubber). Each row is badged with the signal(s) it
// fired via a per-page query, and opens the prompt's detail. Per-signal counts
// + drill-to-one-signal live in the sidebar `signals` facet.
const PAGE_LIMIT = 200;

export default function SignalsConsoleBody({ nav, ctx, slot }) {
  // The listing is BE-sourced (ListSignals) and per-tenant: exactly the tenant's
  // ENABLED detection signals. `ready`/`denied`/`error` drive the lifecycle
  // states a hardcoded array never needed. Memo on the joined set so the query
  // params keep a stable identity across renders (the hook returns a fresh array
  // each call).
  const { ready, denied, error, alertCommands: rawCommands } = useSignalCatalog();
  const commandsKey = rawCommands.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const alertCommands = useMemo(() => rawCommands, [commandsKey]);

  // getTraces projects the enriched prompt text (an ai_sessions sensitive
  // column). A role without that read (e.g. SecOps) must omit it, or the
  // column policy 403s the WHOLE query and the console shows zero firings
  // even when signals fired. Dropping it keeps the rows (summaryUnavailable).
  // Gate fail-CLOSED: a role lacking the grant (or mid caps-load) never
  // requests the column, so the query never 403s; an admin omits it only for
  // the sub-second load window, then refetches with it once caps confirm.
  const caps = useMyPermissions();
  const canReadSensitivePrompts = hasSensitivePermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);

  // Scope to "fired any alert signal": the parent scope + a signal step over the
  // whole alert set. getTraces maps the command ids → namespaces and INNER-joins
  // SignalTags, so the list is exactly the firing prompts.
  const scopePath = useMemo(() => [
    ...(nav?.scopePath || []).filter((s) => s.dim !== "signal" && s.dim !== "signals"),
    ...(alertCommands.length ? [{ dim: "signal", ids: alertCommands }] : []),
  ], [nav?.scopePath, alertCommands]);

  const { data: traces = [], loading } = useQuery(
    "getTraces",
    { scopePath, range: ctx?.range, limit: PAGE_LIMIT, sort: "newest", canReadSensitivePrompts },
    { enabled: alertCommands.length > 0 },
  );

  // Firing-rate chart — one line per alert signal over the range, server
  // time-bucketed (no client scan). The namespace filter inside the query
  // scopes to the alert set, so we pass the parent scope (signal steps dropped).
  const { data: firing } = useQuery(
    "getSignalFiringSeries",
    { scopePath, commands: alertCommands, range: ctx?.range },
    { enabled: alertCommands.length > 0 },
  );
  const firingLayers = firing?.layers || [];
  const hasFiring = firingLayers.some((l) => (l.series || []).some((v) => v > 0));

  const eventIds = useMemo(() => traces.map((t) => t.id).filter(Boolean), [traces]);
  const eventIdsKey = eventIds.join(",");
  const rangeKey = typeof ctx?.range === "object" ? `${ctx?.range?.from}-${ctx?.range?.to}` : String(ctx?.range ?? "");

  // Per-page badges: which alert signal(s) each visible prompt fired.
  const [badges, setBadges] = useState(() => new Map());
  useEffect(() => {
    let live = true;
    if (!eventIds.length) { setBadges(new Map()); return undefined; }
    fetchSignalBadges(eventIds, ctx?.range).then((m) => { if (live) setBadges(m); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdsKey, rangeKey]);

  const total = Number(traces?.total) || traces.length || 0;
  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]" aria-busy={!ready && !denied && !error}>
      <PageHero
        eyebrow="Signals"
        title="Alert console"
        detail="Prompts that fired a security signal in this scope. Select a row to inspect it."
        metrics={[
          { label: "Signals", value: ready ? String(alertCommands.length) : "–", note: "detection signals enabled for this tenant" },
          { label: "Fired", value: ready && alertCommands.length ? String(total) : "–", note: "prompts firing ≥1 signal" },
        ]}
      />
      {denied ? (
        <span className={NOTE_CLASS}>You don’t have permission to view signals. Ask an admin for the Signals read role.</span>
      ) : error ? (
        <span className={NOTE_CLASS}>Couldn’t load signals. Try again shortly.</span>
      ) : !ready ? (
        <span className={NOTE_CLASS}>Loading signals…</span>
      ) : alertCommands.length === 0 ? (
        <span className={NOTE_CLASS}>No detection signals are enabled for this tenant yet.</span>
      ) : null}
      {hasFiring ? (
        <KernelBlock spec={{ kind: "chart", mode: "line", layers: firingLayers, height: 150, range: ctx?.range, title: "Firing rate", compactStandingTooltip: true }} />
      ) : null}
      <TraceTable
        rows={traces}
        signalsByTraceId={badges}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={loading}
        emptyMessage="No prompts fired a security signal in this scope."
      />
    </ScrollArea>
  );
}
